import React from 'react';
import { Calendar, DollarSign, MapPin, CheckSquare, Square, MoreVertical, Users } from 'lucide-react';
import { ASSET_CATEGORIES, ASSET_STATUSES, LOCATIONS } from '../../data/mockData';

const AssetTable = ({ 
  assets, 
  sortConfig, 
  onSort, 
  selectedAssets, 
  onSelectAsset, 
  onSelectAssets 
}) => {
  const getStatusColor = (status) => {
    return ASSET_STATUSES[status]?.color || 'gray';
  };

  const getLocationName = (locationId) => {
    return LOCATIONS.find(loc => loc.id === locationId)?.name || locationId;
  };

  const handleSelectAll = (e) => {
    if (selectedAssets.length === assets.length) {
      onSelectAssets([]);
    } else {
      onSelectAssets(assets.map(asset => asset.id));
    }
  };

  const handleSelect = (assetId) => {
    if (selectedAssets.includes(assetId)) {
      onSelectAssets(selectedAssets.filter(id => id !== assetId));
    } else {
      onSelectAssets([...selectedAssets, assetId]);
    }
  };

  const columns = [
    {
      key: 'select',
      label: (
        <button onClick={handleSelectAll}>
          {selectedAssets.length === assets.length ? (
            <CheckSquare className="w-5 h-5 text-[#43302b]" />
          ) : (
            <Square className="w-5 h-5 text-gray-400" />
          )}
        </button>
      ),
      render: (asset) => (
        <button onClick={() => handleSelect(asset.id)}>
          {selectedAssets.includes(asset.id) ? (
            <CheckSquare className="w-5 h-5 text-[#43302b]" />
          ) : (
            <Square className="w-5 h-5 text-gray-400" />
          )}
        </button>
      )
    },
    {
      key: 'name',
      label: 'Asset Name',
      sortable: true,
      render: (asset) => (
        <div>
          <p className="font-medium text-gray-900">{asset.name}</p>
          <p className="text-sm text-gray-500">{asset.id}</p>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (asset) => {
        const category = ASSET_CATEGORIES.find(cat => cat.id === asset.category);
        return (
          <div className="flex items-center gap-2">
            {category?.icon && React.createElement(category.icon, { className: 'w-4 h-4' })}
            <span>{category?.label || asset.category}</span>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (asset) => (
        <span
          className={`
            inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
            bg-${getStatusColor(asset.status)}-100
            text-${getStatusColor(asset.status)}-700
          `}
        >
          {ASSET_STATUSES[asset.status]?.label || asset.status}
        </span>
      )
    },
    {
      key: 'assignment',
      label: 'Assignment',
      sortable: true,
      render: (asset) => {
        if (asset.assignmentType === 'location') {
          return (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span>{getLocationName(asset.location)}</span>
            </div>
          );
        } else {
          return (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              <div className="flex flex-col">
                <span className="text-sm font-medium">{asset.assignedTo.name}</span>
                <span className="text-xs text-gray-500">{asset.assignedTo.department}</span>
              </div>
            </div>
          );
        }
      }
    },
    {
      key: 'currentValue',
      label: 'Value',
      sortable: true,
      render: (asset) => (
        <div className="flex items-center gap-1">
          <DollarSign className="w-4 h-4 text-gray-400" />
          <span>${asset.currentValue.toLocaleString()}</span>
        </div>
      )
    },
    {
      key: 'purchaseDate',
      label: 'Purchase Date',
      sortable: true,
      render: (asset) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span>{new Date(asset.purchaseDate).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: '',
      render: (asset) => (
        <button className="p-1 hover:bg-gray-100 rounded-lg">
          <MoreVertical className="w-4 h-4 text-gray-400" />
        </button>
      )
    }
  ];

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                onClick={() => column.sortable && onSort(column.key)}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {assets.map((asset) => (
            <tr
              key={asset.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => onSelectAsset(asset)}
            >
              {columns.map((column) => (
                <td
                  key={`${asset.id}-${column.key}`}
                  className="px-6 py-4 whitespace-nowrap"
                  onClick={(e) => {
                    if (column.key === 'select') {
                      e.stopPropagation();
                    }
                  }}
                >
                  {column.render(asset)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AssetTable; 